import { Box, Stack, linearProgressClasses } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useMutationQueue } from "../../../../service/queryHooks/campaignBookings/mutations/useMutationQueue";
import { LinearProgress } from "../atoms/LinearProgress";
import { SnackbarBlock } from "./snackbars/SnackbarBlock";

const SAVING_MESSAGES = [
  "Almost there. We're doing the boring bits so you can get on with the fun stuff.",
  "Please be patient while we hunt down the latest version. No, the latest latest version.",
  "One moment. We're coordinating agencies, media owners, creatives and clients. What could possibly go wrong?",
  "Please be patient while we calculate how many spreadsheets you've avoided opening today.",
  "Good things come to those who wait.",
  "Great things come to those who wait for loading bars.",
] as const;

const MESSAGE_ROTATION_MS = 10_000;

// Fixed layout sizes so the notification box never resizes when messages rotate.
const TITLE_HEIGHT = "1.8125rem"; // 1.5rem font × 1.2 line-height
const PENDING_ROW_HEIGHT = "1.5rem"; // 1.25rem font × 1.2 line-height
const PROGRESS_BAR_WIDTH = 160;
const PROGRESS_BAR_HEIGHT = 8;
const STATUS_MESSAGE_DIVIDER_HEIGHT = "25px"; // mt(12) + border(1) + mb(12)
const MESSAGE_ROW_HEIGHT = "3rem"; // two lines at 1rem / 1.5 line-height
const TITLE_TOP_MARGIN = "10px";
const CONTENT_HEIGHT = `calc(${TITLE_TOP_MARGIN} + ${TITLE_HEIGHT} + 4px + ${PENDING_ROW_HEIGHT} + ${STATUS_MESSAGE_DIVIDER_HEIGHT} + ${MESSAGE_ROW_HEIGHT})`;

const pickRandomSavingMessage = (exclude?: string): string => {
  const candidates = exclude
    ? SAVING_MESSAGES.filter((message) => message !== exclude)
    : SAVING_MESSAGES;

  return candidates[Math.floor(Math.random() * candidates.length)];
};

export const MutationQueueStatus: React.FC = () => {
  const { state } = useMutationQueue();
  const [message, setMessage] = useState(pickRandomSavingMessage);

  useEffect(() => {
    if (state.queueLength === 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setMessage((currentMessage) => pickRandomSavingMessage(currentMessage));
    }, MESSAGE_ROTATION_MS);

    return () => window.clearInterval(intervalId);
  }, [state.queueLength]);

  if (state.queueLength === 0) {
    return null;
  }

  const queueText =
    state.queueLength === 1
      ? `Pending ${state.queueLength} update`
      : `Pending ${state.queueLength} updates`;

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1400,
        width: 640,
        maxWidth: 640,
        "& > .MuiStack-root": {
          width: "100%",
          py: "unset",
          pt: 3,
          pb: 2,
          alignItems: "flex-start",
          mt: 0,
          mb: 0,
          "& > *": {
            mt: 0,
            mb: 0,
          },
        },
        "& [data-pw='snackbar-message']": {
          padding: 0,
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: theme.spacing(6),
        },
      })}
    >
      <SnackbarBlock
        message={
          <Stack
            sx={{
              width: "100%",
              height: CONTENT_HEIGHT,
              flexShrink: 0,
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                height: TITLE_HEIGHT,
                mt: TITLE_TOP_MARGIN,
                fontSize: "1.5rem",
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              Saving your changes
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                mt: 0.5,
                height: PENDING_ROW_HEIGHT,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "1.25rem",
                  lineHeight: 1.2,
                  fontWeight: 700,
                }}
              >
                {queueText}
              </Box>
              <LinearProgress
                sx={{
                  width: PROGRESS_BAR_WIDTH,
                  height: PROGRESS_BAR_HEIGHT,
                  flexShrink: 0,
                  borderRadius: 12,
                  [`&.${linearProgressClasses.colorPrimary}`]: {
                    backgroundColor: "rgba(255, 255, 255, 0.35)",
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    borderRadius: 12,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  },
                }}
              />
            </Stack>
            <Box
              sx={{
                width: "100%",
                borderBottom: "1px solid rgba(255, 255, 255, 0.35)",
                mt: 1.5,
                mb: 1.5,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                width: "100%",
                height: MESSAGE_ROW_HEIGHT,
                fontSize: "1rem",
                lineHeight: 1.5,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {message}
            </Box>
          </Stack>
        }
        severity="info"
      />
    </Box>
  );
};
